import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { instantTranslate, instantConvert } from '@/lib/api';
import { Languages, ArrowRightLeft, X, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function InstantTools() {
  const [activeTool, setActiveTool] = useState(null); // 'translate' or 'convert'
  const [loading, setLoading] = useState(false);
  
  // Translation state
  const [translateText, setTranslateText] = useState('');
  const [translateResult, setTranslateResult] = useState(null);
  const [targetLang, setTargetLang] = useState('fr');
  
  // Conversion state
  const [convertValue, setConvertValue] = useState('');
  const [convertCategory, setConvertCategory] = useState('currency');
  const [fromUnit, setFromUnit] = useState('USD');
  const [toUnit, setToUnit] = useState('EUR');
  const [convertResult, setConvertResult] = useState(null);
  
  const [copied, setCopied] = useState(false);

  const handleTranslate = async () => {
    if (!translateText.trim()) return;
    
    setLoading(true);
    try {
      const res = await instantTranslate(translateText, targetLang, null, null);
      setTranslateResult(res.data);
    } catch (error) {
      toast.error('Erreur de traduction');
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = async () => {
    if (!convertValue) return;
    
    setLoading(true);
    try {
      const res = await instantConvert(parseFloat(convertValue), fromUnit, toUnit, convertCategory);
      setConvertResult(res.data);
    } catch (error) {
      toast.error('Erreur de conversion');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copié !');
    setTimeout(() => setCopied(false), 2000);
  };

  const currencies = ['EUR', 'USD', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'CNY'];
  const distances = [
    { value: 'km', label: 'Kilomètres' },
    { value: 'mi', label: 'Miles' },
    { value: 'm', label: 'Mètres' },
    { value: 'ft', label: 'Pieds' }
  ];
  const temperatures = [
    { value: 'C', label: '°C' },
    { value: 'F', label: '°F' }
  ];

  if (!activeTool) {
    return (
      <div className="flex gap-2 mb-4">
        {/* Mini Icons */}
        <button
          onClick={() => setActiveTool('translate')}
          className="flex items-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:opacity-90 transition-opacity"
        >
          <Languages className="w-4 h-4" />
          <span className="hidden sm:inline">Traduire</span>
        </button>
        
        <button
          onClick={() => setActiveTool('convert')}
          className="flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:opacity-90 transition-opacity"
        >
          <ArrowRightLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Convertir</span>
        </button>
      </div>
    );
  }

  return (
    <Card className="mb-4 border-2 border-dashed" style={{
      borderColor: activeTool === 'translate' ? '#3b82f6' : '#10b981'
    }}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[#1E293B] flex items-center gap-2">
            {activeTool === 'translate' ? (
              <>
                <Languages className="w-5 h-5 text-blue-500" />
                Instant Translation
              </>
            ) : (
              <>
                <ArrowRightLeft className="w-5 h-5 text-green-500" />
                Instant Conversion
              </>
            )}
          </h3>
          <button
            onClick={() => {
              setActiveTool(null);
              setTranslateResult(null);
              setConvertResult(null);
            }}
            className="text-[#64748B] hover:text-[#1E293B]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Translation Tool */}
        {activeTool === 'translate' && (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[#64748B] mb-1 block">Langue cible</label>
              <select
                value={targetLang}
                onChange={(e) => setTargetLang(e.target.value)}
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
              >
                <option value="fr">🇫🇷 Français</option>
                <option value="en">🇬🇧 Anglais</option>
                <option value="es">🇪🇸 Espagnol</option>
                <option value="it">🇮🇹 Italien</option>
                <option value="de">🇩🇪 Allemand</option>
                <option value="pt">🇵🇹 Portugais</option>
                <option value="ja">🇯🇵 Japonais</option>
                <option value="zh">🇨🇳 Chinois</option>
              </select>
            </div>

            <div>
              <label className="text-xs text-[#64748B] mb-1 block">Texte à traduire</label>
              <textarea
                value={translateText}
                onChange={(e) => setTranslateText(e.target.value)}
                placeholder="Menu, panneau, conversation..."
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm resize-none"
                rows={3}
              />
            </div>

            <button
              onClick={handleTranslate}
              disabled={loading || !translateText.trim()}
              className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-2 rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Traduction...
                </>
              ) : (
                <>
                  <Languages className="w-4 h-4" />
                  Traduire
                </>
              )}
            </button>

            {translateResult && (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-xs text-blue-600 mb-1">
                      {translateResult.source_lang} → {translateResult.target_lang}
                    </p>
                    <p className="text-sm text-[#1E293B] font-medium">
                      {translateResult.translation}
                    </p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(translateResult.translation)}
                    className="ml-2 p-2 hover:bg-blue-100 rounded"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4 text-blue-500" />
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Conversion Tool */}
        {activeTool === 'convert' && (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[#64748B] mb-1 block">Catégorie</label>
              <select
                value={convertCategory}
                onChange={(e) => {
                  setConvertCategory(e.target.value);
                  // Reset units
                  if (e.target.value === 'currency') {
                    setFromUnit('USD');
                    setToUnit('EUR');
                  } else if (e.target.value === 'distance') {
                    setFromUnit('km');
                    setToUnit('mi');
                  } else if (e.target.value === 'temperature') {
                    setFromUnit('C');
                    setToUnit('F');
                  }
                }}
                className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
              >
                <option value="currency">💱 Devises</option>
                <option value="distance">📏 Distances</option>
                <option value="temperature">🌡️ Températures</option>
              </select>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-xs text-[#64748B] mb-1 block">Valeur</label>
                <input
                  type="number"
                  value={convertValue}
                  onChange={(e) => setConvertValue(e.target.value)}
                  placeholder="100"
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
                />
              </div>

              <div>
                <label className="text-xs text-[#64748B] mb-1 block">De</label>
                <select
                  value={fromUnit}
                  onChange={(e) => setFromUnit(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
                >
                  {convertCategory === 'currency' && currencies.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                  {convertCategory === 'distance' && distances.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                  {convertCategory === 'temperature' && temperatures.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs text-[#64748B] mb-1 block">Vers</label>
                <select
                  value={toUnit}
                  onChange={(e) => setToUnit(e.target.value)}
                  className="w-full px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm"
                >
                  {convertCategory === 'currency' && currencies.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                  {convertCategory === 'distance' && distances.map(d => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                  {convertCategory === 'temperature' && temperatures.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              onClick={handleConvert}
              disabled={loading || !convertValue}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white py-2 rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Conversion...
                </>
              ) : (
                <>
                  <ArrowRightLeft className="w-4 h-4" />
                  Convertir
                </>
              )}
            </button>

            {convertResult && (
              <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold text-green-700">
                      {convertResult.converted_value} {convertResult.converted_unit}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      {convertResult.formula}
                    </p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(convertResult.converted_value.toString())}
                    className="p-2 hover:bg-green-100 rounded"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-green-600" />
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
